import { observable, runInAction } from 'mobx';

import { isAudioThenVideoEnabled } from './audioThenVideoFlag';

/**
 * Switch for the Audio Sidecar upload path (PLAT-11697, Faster TTFE Leg 3):
 * demux the audio track on the creator's device at file-select, upload it
 * ahead of the Original and start the audio work when it lands.
 *
 * Two switches, both required:
 * - LaunchDarkly `story-upload-audio-sidecar` (`storyUploadAudioSidecar`
 *   through the React SDK), synced into this module by
 *   `useAudioSidecarFlagSync` at the app root. Defaults OFF: a flag that is
 *   missing or not yet evaluated leaves the Sidecar off; only an explicit
 *   `true` turns it on.
 * - The Faster TTFE kill switch (`audioThenVideoFlag.ts`). Starting the
 *   pipeline early is pointless when nothing opens at transcript-ready.
 *
 * Dev override: `localStorage['sidecar.enabled'] = '1'` forces the Sidecar
 * ON regardless of both flags (remove the key to go back to the flags) and
 * also lifts the > 4 GiB `co64` skip so the demo fixtures can take the
 * Sidecar Path (see `demuxAudioSidecar.ts`). For local and preview testing
 * only — it never ships as a default.
 *
 * Held in a MobX box so observers re-render when the LD value lands.
 */
export const AUDIO_SIDECAR_FLAG_KEY = 'story-upload-audio-sidecar';
export const AUDIO_SIDECAR_DEV_OVERRIDE_KEY = 'sidecar.enabled';

const flagOn = observable.box(false);

/** Anything but an explicit `true` counts as off (see module doc). */
export function setAudioSidecarFlag(value: unknown): void {
  runInAction(() => flagOn.set(value === true));
}

/** The LaunchDarkly half alone — use {@link isAudioSidecarEnabled} to decide. */
export function isAudioSidecarFlagOn(): boolean {
  return flagOn.get();
}

export function hasAudioSidecarDevOverride(): boolean {
  try {
    return (
      typeof localStorage !== 'undefined' &&
      localStorage.getItem(AUDIO_SIDECAR_DEV_OVERRIDE_KEY) === '1'
    );
  } catch {
    return false;
  }
}

export function isAudioSidecarEnabled(): boolean {
  if (hasAudioSidecarDevOverride()) return true;
  return flagOn.get() && isAudioThenVideoEnabled();
}
